import DOMPurify from 'dompurify'
import type { Range } from 'quill'
import { BlockEmbed } from 'quill/blots/block'
import { Delta } from 'quill/core'
import { Quill } from 'react-quill-new'

import { SUMMARY_TABLE_BLOT } from './consts'

// Quill's table model drops header rows and rewrites each cell onto its own line, so the
// summary editor keeps each generated <table> as one block whose cells stay editable.

const tableMarkup = (root: ParentNode) => {
  const table = root instanceof HTMLTableElement ? root : root.querySelector('table')
  if (!table) return ''
  const clone = table.cloneNode(true) as HTMLTableElement
  clone.querySelectorAll('[contenteditable]').forEach((cell) => {
    cell.removeAttribute('contenteditable')
  })
  return clone.outerHTML
}

// Quill handles keystrokes for the whole editor. A cell edit has to stay in the cell.
const keepQuillOut = (event: Event) => {
  event.stopPropagation()
}

export const pastePlainText = (event: ClipboardEvent) => {
  event.stopPropagation()
  event.preventDefault()
  const text = event.clipboardData?.getData('text/plain') ?? ''
  const selection = document.getSelection()
  if (!text || !selection || selection.rangeCount === 0) return
  const range = selection.getRangeAt(0)
  const root = event.currentTarget
  if (!(root instanceof Node) || !root.contains(range.commonAncestorContainer)) return
  range.deleteContents()
  const fragment = document.createDocumentFragment()
  text
    .replace(/\r\n/g, '\n')
    .split('\n')
    .forEach((line, index) => {
      if (index > 0) fragment.append(document.createElement('br'))
      fragment.append(document.createTextNode(line))
    })
  range.insertNode(fragment)
  selection.collapseToEnd()
}

class SummaryTableBlot extends BlockEmbed {
  static blotName = SUMMARY_TABLE_BLOT
  static className = 'ql-summary-table'
  static tagName = 'DIV'

  static create(value: string) {
    const node = super.create(value) as HTMLElement
    node.setAttribute('contenteditable', 'false')
    // Report content is stored as-is, so it must be sanitized before it reaches the live DOM.
    node.innerHTML = DOMPurify.sanitize(value)
    node.querySelectorAll('th, td').forEach((cell) => {
      cell.setAttribute('contenteditable', 'true')
    })
    node.addEventListener('keydown', keepQuillOut)
    node.addEventListener('beforeinput', keepQuillOut)
    node.addEventListener('compositionstart', keepQuillOut)
    node.addEventListener('compositionupdate', keepQuillOut)
    node.addEventListener('compositionend', keepQuillOut)
    node.addEventListener('copy', keepQuillOut)
    node.addEventListener('cut', keepQuillOut)
    node.addEventListener('paste', pastePlainText)
    return node
  }

  static value(domNode: HTMLElement) {
    return tableMarkup(domNode)
  }
}

Quill.register(SummaryTableBlot, true)

export const isNativeSelectionInSummaryTable = () => {
  const node = document.getSelection()?.anchorNode
  const el = node instanceof Element ? node : node?.parentElement
  return !!el?.closest('.ql-summary-table')
}

type BindingThis = { quill: Quill }

// Quill's default Backspace/Delete removes a neighbouring embed whole, which for a summary
// table means one key press wipes the table.
export const summaryTableKeyboardBindings = {
  summaryTableBackspace: {
    key: 'Backspace',
    collapsed: true,
    offset: 0,
    handler(this: BindingThis, range: Range) {
      if (range.index === 0) return true
      const [previous] = this.quill.getLine(range.index - 1)
      return !(previous instanceof SummaryTableBlot)
    },
  },
  summaryTableDelete: {
    key: 'Delete',
    collapsed: true,
    handler(this: BindingThis, range: Range) {
      const [line, offset] = this.quill.getLine(range.index)
      if (!line || offset < line.length() - 1) return true
      const [next] = this.quill.getLine(range.index + 1)
      return !(next instanceof SummaryTableBlot)
    },
  },
}

export const preserveSummaryTable = (node: Node) => {
  if (!(node instanceof HTMLTableElement)) return new Delta()
  return new Delta().insert({ [SUMMARY_TABLE_BLOT]: tableMarkup(node) })
}

// The wrapper exists only while editing. Saved reports keep the table itself.
export const htmlForSummarySave = (root: HTMLElement) => {
  const clone = root.cloneNode(true) as HTMLElement
  clone.querySelectorAll('.ql-summary-table').forEach((node) => {
    const html = tableMarkup(node)
    if (!html) return
    const holder = document.createElement('template')
    holder.innerHTML = html
    node.replaceWith(holder.content)
  })
  return clone.innerHTML
}
